import { createAppThunk, RootState } from '@mse-reg/app/store';
import {
  AcademicYearDto,
  FileImportDto,
  hasAnyRole,
  IImportClient,
  ModuleRegistrationInfoDto,
  PhaseDto,
  RegistrationImport,
  RegistrationValidationDto,
  RoleType,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import { semesterSelectionActions } from '../selections/semester-selection.slice';

// Async Thunks
const getValidationAsync = createAppThunk(
  'import-requests/GET_VALIDATION',
  async (id: number, deps: { importClient: IImportClient }) => {
    if (id > 0) {
      return await deps.importClient.getImport(id);
    }
    return null;
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const countPendingImportsAsync = createAppThunk(
  'import-requests/COUNT_PENDING',
  async (_: void, deps: { importClient: IImportClient }) => {
    return await deps.importClient.countPendingImports();
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const getPendingImportsAsync = createAppThunk(
  'import-requests/GET_PENDING_IMPORTS',
  async (_: void, deps: { importClient: IImportClient }, _dispatch, getState) => {
    const { selectedAcademicYear } = (getState() as RootState).semesterSelection;
    return await deps.importClient.getPendingImports(
      selectedAcademicYear?.id ?? 0,
      0,
      0,
    );
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const downloadAsync = createAppThunk(
  'import-requests/DOWNLOAD',
  async (id: number, deps: { importClient: IImportClient }, dispatch) => {
    dispatch(importRequestsActions.selectImport(id));
    await dispatch(getValidationAsync(id));

    const fileResponse = await deps.importClient.getOriginalFile(id);
    saveAs(fileResponse.data, fileResponse.fileName);
    return fileResponse;
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const rejectAsync = createAppThunk(
  'import-requests/REJECT',
  async (id: number, deps: { importClient: IImportClient }, dispatch) => {
    dispatch(importRequestsActions.selectImport(0));
    const success = await deps.importClient.reject(id);
    await dispatch(getPendingImportsAsync(undefined));
    return success;
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const importAsync = createAppThunk(
  'import-requests/IMPORT',
  async (id: number, deps: { importClient: IImportClient }, dispatch, getState) => {
    dispatch(importRequestsActions.selectImport(id));
    const validationResult = await deps.importClient.import(id);

    if (validationResult.canSubmit) {
      if (hasAnyRole((getState() as RootState).auth.account?.roles, [RoleType.REGISTRATIONADMIN])) {
        await Promise.all([dispatch(getPendingImportsAsync(undefined)), dispatch(countPendingImportsAsync())]);
      } else {
        await dispatch(getPendingImportsAsync(undefined));
      }
      dispatch(importRequestsActions.selectImport(0));
    }

    return validationResult;
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const resetValidationAsync = createAppThunk(
  'import-requests/RESET_VALIDATION',
  async (id: number, deps: { importClient: IImportClient }, dispatch) => {
    dispatch(importRequestsActions.selectImport(id));
    const result = await deps.importClient.resetValidation(id);
    dispatch(importRequestsActions.setIsUnsaved(true));
    return result;
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const revalidateSubmittedAsync = createAppThunk(
  'import-requests/REVALIDATE_SUBMITTED',
  async (registrationImport: RegistrationImport, deps: { importClient: IImportClient }, dispatch) => {
    dispatch(importRequestsActions.selectImport(registrationImport.importDbId));
    const result = await deps.importClient.revalidateSubmitted(registrationImport);

    if (result.canSubmit) {
      dispatch(importRequestsActions.setIsUnsaved(false));
    }
    return result;
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const setFilterAsync = createAppThunk(
  'import-requests/SET_FILTER',
  async (
    academicYear: AcademicYearDto | undefined,
    _deps,
    dispatch,
  ) => {
    dispatch(
      semesterSelectionActions.setSemesterFilter({
        academicYear,
        allowEmpty: 'all',
        priority: 'academicYear',
      }),
    );
    dispatch(importRequestsActions.clearSelectedResult());
    return await dispatch(getPendingImportsAsync()).unwrap();
  },
);

const selectImportAsync = createAppThunk(
  'import-requests/SELECT_IMPORT_ASYNC',
  async (id: number | undefined, _deps, dispatch) => {
    dispatch(importRequestsActions.selectImport(id ?? 0));
    if (id && id > 0) {
      return await dispatch(getValidationAsync(id)).unwrap();
    }
    return null;
  },
);

type IImportRequestsState = {
  loading: boolean;
  errorMessage: string | null;
  pendingImports: FileImportDto[];
  selectedResult: RegistrationValidationDto | null;
  pendingImportsCount: number;
  selectedImport: FileImportDto | null;
  mainContentLoading: boolean;
  isUnsaved: boolean;
  previewLoading: boolean;
  phase: PhaseDto | null;
  selectedRegistration: ModuleRegistrationInfoDto | null;
  moduleRegistrationLoading: boolean;
};

const initialState: IImportRequestsState = {
  loading: false,
  errorMessage: null,
  pendingImports: [],
  selectedResult: null,
  isUnsaved: false,
  pendingImportsCount: 0,
  selectedImport: null,
  mainContentLoading: true,
  previewLoading: false,
  phase: null,
  selectedRegistration: null,
  moduleRegistrationLoading: false,
};

export type ImportRequestsState = Readonly<typeof initialState>;

const slice = createSlice({
  name: 'importRequests',
  initialState,
  reducers: {
    selectImport(state, action: PayloadAction<number>) {
      state.selectedImport = state.pendingImports?.find((p) => p.id === action.payload) ?? null;
      state.selectedResult = null;
    },
    selectPhase(state, action: PayloadAction<PhaseDto | undefined>) {
      state.phase = action.payload ?? null;
    },
    setMainContentLoading(state, action: PayloadAction<boolean>) {
      state.mainContentLoading = action.payload;
    },
    setIsUnsaved(state, action: PayloadAction<boolean>) {
      state.isUnsaved = action.payload;
    },
    updateValidationPreview(state, action: PayloadAction<RegistrationImport>) {
      if (state.selectedResult) {
        state.selectedResult = {
          ...state.selectedResult,
          registrationImport: action.payload,
        };
      }
    },
    reduceWarningCount(state) {
      if (state.selectedResult?.count) {
        state.selectedResult = {
          ...state.selectedResult,
          count: {
            ...state.selectedResult.count,
            warnings: state.selectedResult.count.warnings - 1,
            infos: state.selectedResult.count.infos + 1,
            userConfirmations: state.selectedResult.count.userConfirmations + 1,
          },
          canSubmit: state.selectedResult.count.warnings - 1 + state.selectedResult.count.errors === 0,
        };
      }
    },
    clearSelectedResult(state) {
      state.selectedResult = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getPendingImportsAsync.pending, (state) => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addCase(getPendingImportsAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(getPendingImportsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingImports = action.payload;
      })
      .addCase(importAsync.pending, (state) => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addCase(importAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(importAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedResult = action.payload;
      })
      .addCase(downloadAsync.pending, (state) => {
        state.errorMessage = null;
        state.previewLoading = true;
      })
      .addCase(downloadAsync.rejected, (state, action) => {
        state.previewLoading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(downloadAsync.fulfilled, (state) => {
        state.previewLoading = false;
      })
      .addCase(getValidationAsync.pending, (state) => {
        state.errorMessage = null;
        state.previewLoading = true;
      })
      .addCase(getValidationAsync.rejected, (state, action) => {
        state.previewLoading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(getValidationAsync.fulfilled, (state, action) => {
        state.previewLoading = false;
        state.selectedResult = action.payload;
        state.isUnsaved = false;
      })
      .addCase(resetValidationAsync.pending, (state) => {
        state.previewLoading = true;
      })
      .addCase(resetValidationAsync.fulfilled, (state, action) => {
        state.previewLoading = false;
        state.selectedResult = action.payload;
      })
      .addCase(revalidateSubmittedAsync.pending, (state) => {
        state.previewLoading = true;
      })
      .addCase(revalidateSubmittedAsync.fulfilled, (state, action) => {
        state.previewLoading = false;
        state.selectedResult = action.payload;
      })
      .addCase(countPendingImportsAsync.fulfilled, (state, action) => {
        state.pendingImportsCount = action.payload;
      });
  },
});

export const importRequestsReducer = slice.reducer;
export const importRequestsActions = slice.actions;
export const importRequestsThunks = {
  getValidationAsync,
  countPendingImportsAsync,
  getPendingImportsAsync,
  downloadAsync,
  rejectAsync,
  importAsync,
  resetValidationAsync,
  revalidateSubmittedAsync,
  setFilterAsync,
  selectImportAsync,
};

// Legacy module compatibility
const initActions = () => {
  return Object.freeze({
    setMainContentLoading: (loading: boolean) => slice.actions.setMainContentLoading(loading),
    reduceWarningCount: () => slice.actions.reduceWarningCount(),
    resetValidationAsync: (id: number) => resetValidationAsync(id),
    revalidateSubmittedAsync: (registrationImport: RegistrationImport) => revalidateSubmittedAsync(registrationImport),
    countPendingImportsAsync: () => countPendingImportsAsync(),
    selectImportAsync: (id?: number) => selectImportAsync(id),
    getPendingImportsAsync: () => getPendingImportsAsync(),
    downloadAsync: (id: number) => downloadAsync(id),
    rejectAsync: (id: number) => rejectAsync(id),
    importAsync: (id: number) => importAsync(id),
    updateRegistrationImport: (registrationImport: RegistrationImport) =>
      slice.actions.updateValidationPreview(registrationImport),
    setFilterAsync: (academicYear?: AcademicYearDto) =>
      setFilterAsync(academicYear),
  });
};

export type UserManagementActions = ReturnType<typeof initActions>;

export const importRequestsModule = {
  initActions: () => initActions(),
  reducer: slice.reducer,
};
